/**
 * Task Creation Hooks
 *
 * Provides unified interface for creating rule build tasks from different entry points:
 * - Document upload (PDF extraction)
 * - Natural language prompt
 * - Existing rule (edit/clone)
 * - Manual (empty task)
 */

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { baseApi } from '@store/baseApi';
import { useAppDispatch } from '@store/hooks';

import {
  useCreateTaskFromPromptMutation,
  useCreateTaskFromRuleMutation,
  useCreateTaskMutation,
} from '../store/api';

// ============================================================================
// Types
// ============================================================================

export interface CreateTaskResult {
  taskId: string;
  status: string;
  documentId?: string;
  sourceRuleId?: string;
}

export interface UseCreateTaskResult {
  /** Create empty task */
  createEmptyTask: () => Promise<CreateTaskResult>;
  /** Create task from PDF document upload */
  createFromDocument: (file: File) => Promise<CreateTaskResult>;
  /** Create task from natural language prompt */
  createFromPrompt: (prompt: string) => Promise<CreateTaskResult>;
  /** Create task from existing rule (for editing) */
  createFromRule: (ruleId: string) => Promise<CreateTaskResult>;
  /** Whether any creation is in progress */
  isCreating: boolean;
  /** Error message if creation failed */
  error: string | null;
  /** Clear error state */
  clearError: () => void;
}

// ============================================================================
// Custom Document Upload API
// ============================================================================

/**
 * Custom API for document upload that requires FormData.
 * The generated API doesn't support FormData properly, so we need a custom endpoint.
 */
const documentUploadApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    uploadDocumentForRuleBuild: builder.mutation<
      { task_id: string; status?: string; document_id?: string | null },
      { file: File }
    >({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          url: '/v2/provider/rules/build/from-document',
          method: 'POST',
          body: formData,
          // Don't set Content-Type - browser sets it with boundary for multipart
        };
      },
    }),
  }),
  overrideExisting: false,
});

const { useUploadDocumentForRuleBuildMutation } = documentUploadApi;

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook for creating rule build tasks from various entry points
 *
 * @returns Object with creation methods and state
 */
export const useCreateTask = (): UseCreateTaskResult => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // RTK Query mutations
  const [createEmpty] = useCreateTaskMutation();
  const [uploadDocument] = useUploadDocumentForRuleBuildMutation();
  const [createFromPromptMutation] = useCreateTaskFromPromptMutation();
  const [createFromRuleMutation] = useCreateTaskFromRuleMutation();

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Create an empty task
   */
  const createEmptyTask = useCallback(async (): Promise<CreateTaskResult> => {
    setIsCreating(true);
    setError(null);

    try {
      const result = await createEmpty().unwrap();
      return {
        taskId: result.task_id,
        status: result.status,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create task';
      setError(message);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [createEmpty]);

  /**
   * Create task from PDF document upload
   * Returns immediately with taskId - caller should poll for completion
   */
  const createFromDocument = useCallback(
    async (file: File): Promise<CreateTaskResult> => {
      setIsCreating(true);
      setError(null);

      // Validate file type
      if (!file.type.includes('pdf')) {
        const message = 'Please upload a PDF file';
        setError(message);
        setIsCreating(false);
        throw new Error(message);
      }

      try {
        const result = await uploadDocument({ file }).unwrap();
        return {
          taskId: result.task_id,
          status: result.status || 'generating',
          documentId: result.document_id || undefined,
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to upload document';
        setError(message);
        throw err;
      } finally {
        setIsCreating(false);
      }
    },
    [uploadDocument],
  );

  /**
   * Create task from natural language prompt
   * Returns immediately with taskId - caller should poll for completion
   */
  const createFromPrompt = useCallback(
    async (prompt: string): Promise<CreateTaskResult> => {
      setIsCreating(true);
      setError(null);

      if (!prompt.trim()) {
        const message = 'Please enter a rule description';
        setError(message);
        setIsCreating(false);
        throw new Error(message);
      }

      try {
        const result = await createFromPromptMutation({
          fromPromptRequest: { prompt: prompt.trim() },
        }).unwrap();
        return {
          taskId: result.task_id,
          status: result.status || 'generating',
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to generate rule from prompt';
        setError(message);
        throw err;
      } finally {
        setIsCreating(false);
      }
    },
    [createFromPromptMutation],
  );

  /**
   * Create task from existing rule (for editing)
   * Returns immediately with taskId - caller should poll for completion
   */
  const createFromRule = useCallback(
    async (ruleId: string): Promise<CreateTaskResult> => {
      setIsCreating(true);
      setError(null);

      try {
        const result = await createFromRuleMutation({ ruleId }).unwrap();
        return {
          taskId: result.task_id,
          status: result.status || 'generating',
          sourceRuleId: result.source_rule_id,
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load rule for editing';
        setError(message);
        throw err;
      } finally {
        setIsCreating(false);
      }
    },
    [createFromRuleMutation],
  );

  return {
    createEmptyTask,
    createFromDocument,
    createFromPrompt,
    createFromRule,
    isCreating,
    error,
    clearError,
  };
};

/**
 * Hook that creates a task and navigates to the editor
 * Convenience wrapper around useCreateTask for simpler usage
 */
export const useCreateTaskAndNavigate = () => {
  const navigate = useNavigate();
  const taskHooks = useCreateTask();

  const createAndNavigate = useCallback(
    async (
      method: 'empty' | 'document' | 'prompt' | 'rule',
      data?: File | string,
    ): Promise<void> => {
      let result: CreateTaskResult;

      switch (method) {
        case 'empty':
          result = await taskHooks.createEmptyTask();
          break;
        case 'document':
          if (!(data instanceof File)) {
            throw new Error('Document method requires a File');
          }
          result = await taskHooks.createFromDocument(data);
          break;
        case 'prompt':
          if (typeof data !== 'string') {
            throw new Error('Prompt method requires a string');
          }
          result = await taskHooks.createFromPrompt(data);
          break;
        case 'rule':
          if (typeof data !== 'string') {
            throw new Error('Rule method requires a rule ID string');
          }
          result = await taskHooks.createFromRule(data);
          break;
      }

      // Navigate to the task editor
      navigate(`/rule-build/${result.taskId}`);
    },
    [navigate, taskHooks],
  );

  return {
    ...taskHooks,
    createAndNavigate,
  };
};

export default useCreateTask;
